// ignore-string-externalization

import { graphqlAssetToContentful, makeContentfulAsset } from '../Contentful';
import { youtubeThumbnailUrl } from '../Template/socialCard';
import { youtubeIdRegex } from '../utils';
import { isHighDPI } from './videosQuery';
import type {
  VideoFieldsFragment,
  VideoPageFieldsFragment,
} from '../../graphql/generated';

export type ThumbnailImage = {
  url?: string | null;
  title?: string | null;
  description?: string | null;
};

function makeThumbnailAndHeader(image: ThumbnailImage | null | undefined) {
  if (!image?.url)
    return { thumbnail: undefined, header: undefined, asset: null };
  const asset = graphqlAssetToContentful(image);
  const thumbnail = {
    title: image.title,
    description: image.description,
    url: `${image.url}?fm=jpg&fl=progressive&w=${764 / (isHighDPI ? 1 : 2)}`,
  };
  const header = {
    title: image.title,
    description: image.description,
    url: `${image.url}?fm=jpg&fl=progressive&h=764`,
  };
  return { thumbnail, header, asset };
}

// Item shape preserved from Redux store (videos.ts) to avoid changes in consuming components
export function formatVideoItem(item: VideoFieldsFragment) {
  const releaseDateParsed = new Date(item.releaseDate as string);
  // @ts-ignore
  if (releaseDateParsed > Date.now()) return null;

  const videoUrlParsed = youtubeIdRegex.exec(item.videoUrl ?? '');
  const bgColor = item.headerBackgroundColor ?? '#000';
  const { thumbnail, header, asset } = makeThumbnailAndHeader(
    item.thumbnailImage,
  );

  return {
    id: item.sys.id,
    name: item.name,
    urlSlug: item.urlSlug,
    body: item.body,
    videoDuration: item.videoDuration ?? '',
    translated: null,
    thumbnailAsset: asset,
    thumbnailImage: thumbnail,
    largeImage: header,
    youtubeId: videoUrlParsed?.[1] ?? null,
    releaseDate: releaseDateParsed.toISOString(),
    releaseDateUTC: releaseDateParsed.toUTCString(),
    headerBackgroundColor: bgColor,
    headerTextColor: bgColor.toLowerCase() === '#000' ? '#fff' : '#000',
    category: undefined as string | undefined,
  };
}

export function formatVideoPageItem(item: VideoPageFieldsFragment) {
  const videoUrl = item.video?.videoUrl;
  if (!videoUrl) return null;

  const videoUrlParsed = youtubeIdRegex.exec(videoUrl);
  if (!videoUrlParsed?.[1]) return null;

  const name = item.title ?? '';

  let thumbnail;
  let header;
  let asset;
  if (item.thumbnailImage?.url) {
    ({ thumbnail, header, asset } = makeThumbnailAndHeader(
      item.thumbnailImage,
    ));
  } else {
    const thumbUrl = youtubeThumbnailUrl(videoUrl);
    asset = makeContentfulAsset({
      url: thumbUrl as string,
      contentType: 'image/jpeg',
      width: 1280,
      height: 720,
      description: name,
    });
    thumbnail = { url: thumbUrl, description: name };
    header = { url: thumbUrl, description: name };
  }

  return {
    id: item.sys.id,
    name,
    urlSlug: item.urlSlug,
    body: item.description,
    thumbnailAsset: asset,
    thumbnailImage: thumbnail,
    largeImage: header,
    youtubeId: videoUrlParsed[1],
    headerBackgroundColor: '#000',
    headerTextColor: '#fff',
    category: undefined as string | undefined,
    videoDuration: '',
    translated: item.translated ?? null,
  };
}

// Shape (byId, allIds, allSlugs, slugToId) preserved from Redux store to avoid changes in consuming components
export function normalize<T extends { id: string; urlSlug?: string | null }>(
  items: T[],
) {
  return {
    byId: Object.fromEntries(items.map(item => [item.id, { ...item }])),
    allIds: items.map(item => item.id),
    allSlugs: items.map(item => item.urlSlug ?? ''),
    slugToId: Object.fromEntries(
      items.map(item => [item.urlSlug ?? '', item.id]),
    ),
  };
}
